import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SponsorContactService } from '../../core/api-services/sponsor-contact.service';
import { SponsorContactData } from '../../core/response-models/sponsor-contact-data.model';
import { environment } from '../../../environments/environment';
import { PhoneFormatPipe } from '../pipes';

/**
 * HowToBuyModalComponent
 *
 * Reusable modal that displays sponsor contact information (locations,
 * representatives, website, or a mix) based on the sponsor's contact type.
 *
 * Usage from Chemical Book:
 *   <app-how-to-buy-modal [sponsorId]="selectedSponsorId" [visible]="showModal" (close)="closeModal()">
 *
 * Usage from Acres Advantage (dummy data mode):
 *   <app-how-to-buy-modal [sponsorId]="selectedSponsorId" [visible]="showModal"
 *     [dummyData]="dummyContactData" (close)="closeModal()">
 */
@Component({
  selector: 'app-how-to-buy-modal',
  standalone: true,
  imports: [CommonModule, PhoneFormatPipe],
  templateUrl: './how-to-buy-modal.component.html',
  styleUrls: ['./how-to-buy-modal.component.scss']
})
export class HowToBuyModalComponent implements OnChanges {
  private sponsorContactService = inject(SponsorContactService);

  /** Sponsor ID to fetch contact data for */
  @Input() sponsorId = '';

  /** Controls modal visibility */
  @Input() visible = false;

  /** Optional dummy data -- bypasses API fetch (used in Acres Advantage preview mode) */
  @Input() dummyData: SponsorContactData | null = null;

  /** Emits when the modal is closed */
  @Output() close = new EventEmitter<void>();

  /** Active sub-tab for mixed contact type (locations vs representatives) */
  activeSubTab: 'locations' | 'representatives' = 'locations';

  /** Service signals */
  loading = this.sponsorContactService.loading;
  error = this.sponsorContactService.error;
  contactData = this.sponsorContactService.contactData;

  /** Default fallback message for contact_form_only sponsors */
  readonly DEFAULT_CONTACT_MESSAGE = 'Contact this sponsor for pricing in your area.';

  ngOnChanges(changes: SimpleChanges): void {
    // Fetch contacts when modal becomes visible with a sponsor ID
    if (changes['visible'] || changes['sponsorId'] || changes['dummyData']) {
      if (this.visible && this.sponsorId) {
        // Reset sub-tab
        this.activeSubTab = 'locations';

        if (this.dummyData) {
          // Use dummy data directly -- no API call needed
          return;
        }

        this.sponsorContactService.fetchContacts(this.sponsorId);
      }
    }
  }

  /**
   * Get the resolved contact data -- either from dummy data or the service signal
   */
  get data(): SponsorContactData | null {
    if (this.dummyData) {
      return this.dummyData;
    }
    return this.contactData();
  }

  /**
   * Get the contact message with fallback to default
   */
  get displayMessage(): string {
    return this.data?.contactMessage || this.DEFAULT_CONTACT_MESSAGE;
  }

  /**
   * Format a location address as a single line
   */
  formatAddress(location: { street1: string; street2?: string | null; city: string; state: string; zipCode: string }): string {
    const parts = [location.street1];
    if (location.street2) {
      parts.push(location.street2);
    }
    parts.push(`${location.city}, ${location.state} ${location.zipCode}`);
    return parts.join(', ');
  }

  /**
   * Resolve a logo path to a full CDN URL
   */
  getLogoFullUrl(logoUrl: string | null | undefined): string {
    if (!logoUrl) return '';
    if (logoUrl.startsWith('http')) return logoUrl;
    return `${environment.appCDN}/${logoUrl}`;
  }

  /**
   * Get initials from a name (e.g., "Aaron Burke" -> "AB")
   */
  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  /**
   * Split a territory string into individual badges (only if comma-separated).
   * Returns empty array for single-value/sentence territories (displayed as plain text instead).
   */
  splitTerritory(territory: string | null): string[] {
    if (!territory) return [];
    if (!territory.includes(',')) return [];
    return territory.split(',').map(s => s.trim()).filter(s => s.length > 0);
  }

  /**
   * Check if a rep has any contact info (phone or email)
   */
  hasContactInfo(rep: { phone: string | null; email: string | null }): boolean {
    return !!(rep.phone || rep.email);
  }

  /**
   * Set the active sub-tab (for mixed contact type)
   */
  setSubTab(tab: 'locations' | 'representatives'): void {
    this.activeSubTab = tab;
  }

  /**
   * Close the modal
   */
  onClose(): void {
    this.close.emit();
  }

  /**
   * Stop click propagation on the modal dialog (prevents backdrop close)
   */
  onDialogClick(event: MouseEvent): void {
    event.stopPropagation();
  }
}
